
import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockAccounts, mockRoles, mockDepartments } from './mockData';
import type { Account, AccountFormData, SearchFilters } from './types';
import SearchFiltersComponent from './components/SearchFilters';
import AccountModal from './components/AccountModal';
import ConfirmModal from '../roles/components/ConfirmModal';

export default function AccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>(mockAccounts);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>(mockAccounts);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>();
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const handleSearch = (filters: SearchFilters) => {
    let filtered = [...accounts];

    if (filters.keyword) {
      filtered = filtered.filter(
        (acc) =>
          acc.username.includes(filters.keyword) ||
          acc.phone === filters.keyword
      );
    }

    if (filters.roleIds.length > 0) {
      filtered = filtered.filter((acc) => filters.roleIds.includes(acc.roleId));
    }

    if (filters.departmentIds.length > 0) {
      filtered = filtered.filter((acc) => filters.departmentIds.includes(acc.departmentId));
    }

    if (filters.status) {
      filtered = filtered.filter((acc) => acc.status === (filters.status === 'true'));
    }

    if (filters.startDate) {
      filtered = filtered.filter((acc) => acc.createdAt >= filters.startDate);
    }

    if (filters.endDate) {
      filtered = filtered.filter((acc) => acc.createdAt <= filters.endDate);
    }

    setFilteredAccounts(filtered);
  };

  const handleCreate = () => {
    setEditingAccount(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleSave = (data: AccountFormData) => {
    if (editingAccount) {
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.id === editingAccount.id
            ? {
                ...acc,
                ...data,
                departmentName: mockDepartments.find((d) => d.id === data.departmentId)?.name || '',
                roleName: mockRoles.find((r) => r.id === data.roleId)?.name || '',
              }
            : acc
        )
      );
      setFilteredAccounts((prev) =>
        prev.map((acc) =>
          acc.id === editingAccount.id
            ? {
                ...acc,
                ...data,
                departmentName: mockDepartments.find((d) => d.id === data.departmentId)?.name || '',
                roleName: mockRoles.find((r) => r.id === data.roleId)?.name || '',
              }
            : acc
        )
      );
    } else {
      const newAccount: Account = {
        id: Math.max(...accounts.map((a) => a.id)) + 1,
        ...data,
        departmentName: mockDepartments.find((d) => d.id === data.departmentId)?.name || '',
        roleName: mockRoles.find((r) => r.id === data.roleId)?.name || '',
        status: true,
        lastLoginTime: '-',
        createdAt: new Date().toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(/\//g, '-'),
      };
      setAccounts((prev) => [newAccount, ...prev]);
      setFilteredAccounts((prev) => [newAccount, ...prev]);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (account: Account) => {
    setConfirmModal({
      isOpen: true,
      title: '删除账号',
      message: `确定要删除账号"${account.username}"吗？删除后将无法恢复。`,
      onConfirm: () => {
        setAccounts((prev) => prev.filter((acc) => acc.id !== account.id));
        setFilteredAccounts((prev) => prev.filter((acc) => acc.id !== account.id));
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleToggleStatus = (account: Account) => {
    const newStatus = !account.status;
    setConfirmModal({
      isOpen: true,
      title: newStatus ? '启用账号' : '禁用账号',
      message: `确定要${newStatus ? '启用' : '禁用'}账号"${account.username}"吗？`,
      onConfirm: () => {
        setAccounts((prev) =>
          prev.map((acc) => (acc.id === account.id ? { ...acc, status: newStatus } : acc))
        );
        setFilteredAccounts((prev) =>
          prev.map((acc) => (acc.id === account.id ? { ...acc, status: newStatus } : acc))
        );
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">账号管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理后台用户账号</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            创建账号
          </button>
        </div>

        <SearchFiltersComponent onSearch={handleSearch} />

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    用户ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    用户名
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    真实姓名
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    手机号
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    邮箱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    所属角色
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    所属部门
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    最后登录时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {account.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.realName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.roleName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.departmentName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {account.lastLoginTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={account.status}
                          onChange={() => handleToggleStatus(account)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEdit(account)}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDelete(account)}
                          className="text-red-600 hover:text-red-700 cursor-pointer whitespace-nowrap"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AccountModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        account={editingAccount}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
    </MainLayout>
  );
}
